// @ts-strict-ignore
import { Box, List, sprinkles, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

import { MenuItem } from "./Item";
import { type SidebarMenuItem } from "./types";
import { isMenuActive } from "./utils";

interface Props {
  menuItem: SidebarMenuItem;
}

// Light-tint active treatment: soft indigo background with indigo accent
// text/icon, no heavy fill or outer glow.
const ACTIVE_BG = "#EEF0FF";
const ACTIVE_FG = "#4F46E5";
const HOVER_BG = "#F5F7FF";
const HOVER_FG = "#4F46E5";

export const ItemGroup = ({ menuItem }: Props) => {
  const { pathname } = useLocation();
  const hasSubmenuActive = menuItem?.children.some(item => isMenuActive(pathname, item));
  const isActive = isMenuActive(pathname, menuItem) && !hasSubmenuActive;
  const isExpanded = isActive || hasSubmenuActive;
  const [hovered, setHovered] = useState(false);

  const handleMenuGroupClick = () => {
    if (menuItem.onClick) {
      menuItem.onClick();
    }
  };

  const baseStyle: React.CSSProperties = { borderRadius: 8 };
  const triggerStyle: React.CSSProperties = isActive
    ? { ...baseStyle, backgroundColor: ACTIVE_BG }
    : hovered
      ? { ...baseStyle, backgroundColor: HOVER_BG }
      : baseStyle;

  const iconColor = isActive
    ? ACTIVE_FG
    : hovered
      ? HOVER_FG
      : (menuItem.iconColor ?? "#6B7280");
  const textColor = isActive ? ACTIVE_FG : hovered ? HOVER_FG : "#374151";

  return (
    <List.ItemGroup defaultExpanded={isExpanded} data-test-id={`menu-list-item`}>
      <List.ItemGroup.Trigger
        paddingX={0}
        paddingRight={0}
        borderRadius={3}
        size="small"
        active={false}
        justifyContent="space-between"
        data-test-id={`menu-item-label-${menuItem.id}`}
        position="relative"
        onClick={handleMenuGroupClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="sidebar-item-group-trigger"
        style={triggerStyle}
      >
        <Link
          replace={isActive}
          to={menuItem?.url ?? ""}
          className={sprinkles({ width: "100%", display: "block" })}
        >
          <Box display="flex" alignItems="center" gap={3} paddingY={2.5} paddingX={3}>
            <Box
              display="flex"
              alignItems="center"
              flexShrink="0"
              style={{ color: iconColor }}
            >
              {menuItem.icon}
            </Box>
            <Text
              size={3}
              fontWeight={isActive ? "bold" : "medium"}
              style={{ color: textColor }}
            >
              {menuItem.label}
            </Text>
            {menuItem.endAdornment && <Box>{menuItem.endAdornment}</Box>}
          </Box>
        </Link>
      </List.ItemGroup.Trigger>

      <List.ItemGroup.Content>
        <Box
          paddingLeft={3}
          marginLeft={4}
          display="flex"
          flexDirection="column"
          marginBottom={1}
          marginTop={1}
          gap={1}
          style={{ borderLeft: "1.5px solid #E5E7EB" }}
        >
          {menuItem.children?.map(child => (
            <MenuItem menuItem={child} key={child.id} />
          ))}
        </Box>
      </List.ItemGroup.Content>
    </List.ItemGroup>
  );
};
